import { Box, Flex, Text, Input } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FigmaMoveIcon from 'components/Icons/FigmaMoveIcon';
import {
  AddBuilderDimension,
  DimensionReorderingData,
  FetchDimensionList,
  getAddDimension,
  getBaseScenario,
  getBuilderDimensions,
  getSelectedDimension,
  handleAddDimensionStatus,
  handleBuilderSelectedDimension,
} from 'redux/BuilderModeSlice';
import apiService from 'services';

import DimensionItemTab from './DimensionItemTab';
import { DimensionsSidebarSkeleton } from './DimensionsSkeletons';
import AddDimensionModal from './AddDimensionModal';
import { accent, font, fontSize, neutral, radius, shadow, text as textToken } from './designTokens';

/** Parses the bulk dimension-items response into a { [dimensionId]: itemCount } map.
 * The API has been observed to return the grouped-by-dimension payload in a few
 * different shapes, so this mirrors the same defensive parsing already used in
 * DesktopElements.tsx for the same endpoint. */
const parseBulkItemCounts = (response: any): Record<number, number> => {
  const counts: Record<number, number> = {};
  const dimensionsArray =
    response?.data?.dimensions || response?.dimensions || response?.data || response;

  if (Array.isArray(dimensionsArray)) {
    dimensionsArray.forEach((dimensionData: any) => {
      const dimId = Number(dimensionData?.dimension_id ?? dimensionData?.id);
      if (!Number.isNaN(dimId) && Array.isArray(dimensionData?.items)) {
        counts[dimId] = dimensionData.items.length;
      }
    });
  } else if (dimensionsArray && typeof dimensionsArray === 'object') {
    Object.keys(dimensionsArray).forEach((key) => {
      const dimensionData = dimensionsArray[key];
      const keyAsNumber = Number.parseInt(key, 10);
      if (Array.isArray(dimensionData)) {
        if (!Number.isNaN(keyAsNumber)) counts[keyAsNumber] = dimensionData.length;
      } else if (Array.isArray(dimensionData?.items)) {
        const dimId = Number(dimensionData?.dimension_id ?? keyAsNumber);
        if (!Number.isNaN(dimId)) counts[dimId] = dimensionData.items.length;
      }
    });
  }
  return counts;
};

const DimensionBuilderSidebar = () => {
  const dispatch: any = useDispatch();
  const parameters: any = useParams().modelId;

  const { modelId } = useParams();

  const builderDimension = useSelector(getBuilderDimensions);
  const addDimension = useSelector(getAddDimension);
  const selectedDimension: any = useSelector(getSelectedDimension);
  const baseScenario: any = useSelector(getBaseScenario);

  const [loader, setLoader] = useState(false);
  const [myDim, setDims] = useState(builderDimension);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemCounts, setItemCounts] = useState<Record<number, number>>({});

  const carouselReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dimensionIds = (myDim || []).map((dim: any) => dim?.id).filter(Boolean);
    if (dimensionIds.length === 0 || !baseScenario?.id) {
      return;
    }
    apiService
      .get_dimension_items_bulk({
        params: {
          dimension_ids: dimensionIds.join(','),
          scenario_id: baseScenario.id,
          onlyitems: false,
        },
      })
      .then((response: any) => {
        setItemCounts(parseBulkItemCounts(response));
      })
      .catch(() => {
        setItemCounts({});
      });
  }, [myDim, baseScenario?.id]);

  useEffect(() => {
    const getDimension = async () => {
      if (parameters) {
        setLoader(true);
        await dispatch(FetchDimensionList(parameters)).then(async (response: any) => {
          if (!selectedDimension) {
            await dispatch(handleBuilderSelectedDimension(response?.payload?.dimensions[0]));
          }
          setLoader(false);
        });
      }
    };
    getDimension();
  }, []);

  useEffect(() => {
    setDims(builderDimension);
  }, [builderDimension]);

  const handleAddDimension = (name: string, description?: string) => {
    const dinName = name.trim();
    if (parameters && dinName) {
      dispatch(
        AddBuilderDimension({
          modelId: parameters,
          data: { name: dinName, description: description?.trim() || '' },
          toaster: { successMessage: false, errorMessage: true },
        }),
      ).then(() => {
        dispatch(FetchDimensionList(parameters));
        dispatch(handleAddDimensionStatus(false));
      });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    const blockIds = builderDimension.map((block: any) => block.id);
    const oldIndex = blockIds.indexOf(active.id);
    const newIndex = blockIds.indexOf(over.id);
    setDims([...arrayMove(builderDimension, oldIndex, newIndex)]);
    const reOrderData = [...arrayMove(builderDimension, oldIndex, newIndex)];
    const ids = reOrderData?.map((item) => item?.id);
    await dispatch(
      DimensionReorderingData({
        modelId,
        request_body: { dimensions: ids },
      }),
    );
    await dispatch(FetchDimensionList(parameters));
  };

  const filteredDims = (myDim || []).filter((dim: any) =>
    (dim?.name || '').toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Flex
      height='100%'
      width='310px'
      flexShrink={0}
      flexDirection='column'
      bg={neutral.surface}
      borderRadius={radius.surface}
      border={`1px solid ${neutral.border}`}
      // A panel that sits flush in the layout (not floating above it) doesn't
      // need a drop shadow — the border already separates it. The old 12px
      // ambient shadow made the page feel like stacked cards.
      boxShadow={shadow.panel}
      position='relative'
      overflow='hidden'
    >
      <Flex alignItems='center' width='100%' gap='12px' px='12px' pt='12px' pb='8px'>
        <Flex
          alignItems='center'
          flex='1 1 auto'
          minW={0}
          gap='6px'
          bg={neutral.surface}
          border={`1px solid ${neutral.border}`}
          borderRadius={radius.control}
          px='8px'
          py='8px'
          transition='border-color 0.12s ease'
          _focusWithin={{ borderColor: accent.border }}
        >
          {/* Search glyph is decoration, not an action — greyscale. */}
          <FigmaSearchIcon width={13} height={13} color={textToken.muted} />
          <Input
            variant='unstyled'
            placeholder='Search dimensions...'
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            fontSize={fontSize.body}
            fontFamily={font.body}
            color={textToken.primary}
            _placeholder={{ color: textToken.muted }}
            sx={{ '&:focus::placeholder': { opacity: 0 } }}
          />
        </Flex>
        <Flex
          alignItems='center'
          gap='6px'
          cursor='pointer'
          flexShrink={0}
          borderRadius={radius.control}
          px='6px'
          py='4px'
          transition='background 0.12s ease'
          _hover={{ bg: accent.subtle }}
          _active={{ bg: accent.subtle }}
          onClick={() => dispatch(handleAddDimensionStatus(true))}
        >
          {/* Genuine action, so it keeps the accent — but the `scale(0.97)`
              press bounce is a consumer-app tic; tools just change fill. */}
          <FigmaPlusIcon width={11} height={11} color={accent.solid} />
          <Text
            fontSize={fontSize.body}
            fontWeight='500'
            fontFamily={font.body}
            color={accent.solid}
            whiteSpace='nowrap'
          >
            Add New
          </Text>
        </Flex>
      </Flex>
      <AddDimensionModal
        isOpen={addDimension}
        onClose={() => dispatch(handleAddDimensionStatus(false))}
        onCreate={handleAddDimension}
      />
      {!loader ? (
        <Flex
          flex='1 0 0'
          gap='0'
          flexDir='column'
          minH={0}
          overflowX='hidden'
          overflowY='auto'
          className='scroll'
          px='12px'
          py='8px'
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            {filteredDims && filteredDims?.length > 0 ? (
              <SortableContext items={filteredDims} strategy={rectSortingStrategy}>
                <Box ref={carouselReference}>
                  <Flex flexDirection='column' gap='0'>
                    {filteredDims?.map((response: any, index: number) => (
                      <React.Fragment key={response?.id ?? index}>
                        <DimensionItemTab
                          dimData={response}
                          itemCount={itemCounts[response?.id]}
                          key={response?.id ?? index}
                        />
                      </React.Fragment>
                    ))}
                  </Flex>
                </Box>
              </SortableContext>
            ) : (
              'Not found'
            )}
          </DndContext>
        </Flex>
      ) : (
        <DimensionsSidebarSkeleton />
      )}
      <Flex
        alignItems='center'
        justifyContent='center'
        gap='8px'
        borderTop={`1px solid ${neutral.borderSubtle}`}
        bg={neutral.surfaceMuted}
        p='8px'
      >
        <Box transform='rotate(90deg)' display='flex'>
          <FigmaMoveIcon width={13} height={13} color={textToken.muted} />
        </Box>
        {/* Passive hint, not an instruction the user must read every visit —
            drop it to label size and muted grey. */}
        <Text
          fontSize={fontSize.label}
          fontWeight='400'
          fontFamily={font.body}
          color={textToken.muted}
          whiteSpace='nowrap'
        >
          Drag to reorder dimensions
        </Text>
      </Flex>
    </Flex>
  );
};

export default DimensionBuilderSidebar;
